import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './ProjectDetail.css';
import linkedinIcon from '../../assets/socials/3d-icon-linkedin.png';
import githubIcon from '../../assets/socials/3d-icon-github.png';
import facebookIcon from '../../assets/socials/3d-icon-facebook.png';
import bulsufindImg from '../../assets/projects/bulsufind.png';
import codingChroniclesImg from '../../assets/projects/coding-chronicles.png';
import bulsuClassroomImg from '../../assets/projects/bulsu-classroom.png';
import barbershopAppImg from '../../assets/projects/barber-appointment.png';

const projects = [
  {
    id: 'barbershop-appointment',
    title: 'Barbershop Appointment System',
    year: '2023',
    role: 'Full-Stack Developer',
    desc: 'Created a prototype of a Barbershop Website System using HTML, CSS, JavaScript, PHP, and MySQL. Developed as a final project for Systems Analysis and Design, the platform enables customers to view available barbers, choose grooming packages, select real-time appointment slots, and manage reservations while offering an administrative backend for staff scheduling.',
    media: [
      { type: 'image', src: barbershopAppImg, alt: 'Barbershop Appointment System Screenshot' },
      { type: 'video', src: 'https://youtu.be/JleOAvKj8Ug', alt: 'Barbershop Appointment System Demo' }
    ],
    tech: ['HTML5', 'CSS3', 'JavaScript', 'PHP', 'MySQL', 'Bootstrap 5', 'AJAX'],
    links: [
      { label: 'Watch Video Demo', url: 'https://youtu.be/JleOAvKj8Ug' }
    ]
  },
  {
    id: 'bulsufind',
    title: 'BulSUFind — Campus Lost & Found Mobile App',
    year: '2024',
    role: 'Lead Mobile & Backend Developer',
    desc: 'A full native Android application engineered for lost and found property management across Bulacan State University campuses. Features include image-based item search, real-time Firebase cloud database synchronization, instant push notifications, and secure peer-to-peer chat for claim coordination.',
    media: [
      { type: 'image', src: bulsufindImg, alt: 'BulSUFind Screenshot' },
      { type: 'video', src: 'https://youtu.be/LOOvYX3b7dA', alt: 'BulSUFind Demo Video' }
    ],
    tech: ['Android SDK', 'Java', 'Firebase Realtime DB', 'Google Cloud APIs', 'REST APIs', 'Cloud Messaging'],
    links: [
      { label: 'Watch Video Demo', url: 'https://youtu.be/LOOvYX3b7dA' }
    ]
  },
  {
    id: 'coding-chronicles',
    title: 'Coding Chronicles — 2D Platformer',
    year: '2024',
    role: 'Lead Programmer & Game Designer',
    desc: 'Led the development of a fast-paced 2D action platformer built with Unity and C#. Programmed core movement physics, collision handling, dynamic trap hazards, and enemy state machines while engineering cross-platform support for PC and Android.',
    media: [
      { type: 'image', src: codingChroniclesImg, alt: 'Coding Chronicles Screenshot' },
      { type: 'video', src: 'https://www.youtube.com/watch?v=CZxOOZRPv8w', alt: 'Coding Chronicles Trailer' }
    ],
    tech: ['Unity Engine', 'C#', 'Game Physics', 'Android SDK', 'Photoshop'],
    links: [
      { label: 'Watch Gameplay Trailer', url: 'https://www.youtube.com/watch?v=CZxOOZRPv8w' }
    ]
  },
  {
    id: 'bulsu-classroom',
    title: 'BulSU Classroom — Assessment Engine',
    year: '2024',
    role: 'Assessment Module Lead',
    desc: 'Engineered an automated quiz and examination system featuring real-time anti-cheating browser focus detection, timer enforcement, randomized question banks, instant grade calculation, and instructor analytics dashboard.',
    media: [
      { type: 'image', src: bulsuClassroomImg, alt: 'BulSU Classroom Screenshot' }
    ],
    tech: ['Python', 'Django', 'JavaScript', 'Browser Focus APIs', 'Bootstrap 5'],
    links: []
  }
];

const ProjectDetail = () => {
  const { id } = useParams();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const project = projects.find(p => p.id === id);

  if (!project) {
    return (
      <main className="project-detail-section not-found">
        <div className="not-found-card">
          <h2>Project Not Found</h2>
          <p>The requested project does not exist or has been relocated.</p>
          <Link to="/" className="btn btn-primary">Return to Home</Link>
        </div>
      </main>
    );
  }

  const videoMedia = project.media.find(m => m.type === 'video');
  const imageMedia = project.media.find(m => m.type === 'image');

  const extractYouTubeId = (url) => {
    if (!url) return '';
    const match = url.match(/[?&]v=([^&#]+)/) || url.match(/youtu\.be\/([^?&#]+)/);
    return match ? match[1] : '';
  };

  const videoId = videoMedia ? extractYouTubeId(videoMedia.src) : null;

  return (
    <main className="project-detail-section">
      <div className="project-detail-container">
        {/* Navigation Bar */}
        <div className="project-detail-top-nav">
          <Link to="/#projects" className="btn btn-secondary back-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Projects
          </Link>
        </div>

        {/* Hero Showcase Grid */}
        <div className="project-detail-bento">
          {/* Main Media Showcase */}
          <div className="detail-media-card">
            {videoId ? (
              <div className="detail-video-wrapper">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1`}
                  title={`${project.title} video`}
                  className="detail-iframe"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            ) : imageMedia ? (
              <div className="detail-image-wrapper">
                <img src={imageMedia.src} alt={project.title} className="detail-image" />
              </div>
            ) : null}
          </div>

          {/* Project Overview Card */}
          <div className="detail-info-card">
            <div className="detail-header">
              <span className="section-tag">Case Study</span>
              <h1 className="detail-title">{project.title}</h1>
            </div>

            <div className="detail-metadata-grid">
              <div className="metadata-item">
                <span className="metadata-label">Timeline</span>
                <span className="metadata-value">{project.year}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Role</span>
                <span className="metadata-value">{project.role}</span>
              </div>
            </div>

            <div className="detail-body">
              <h3 className="detail-section-heading">Overview</h3>
              <p className="detail-description">{project.desc}</p>
            </div>

            {/* Tech Stack */}
            <div className="detail-tech-section">
              <h3 className="detail-section-heading">Technologies Used</h3>
              <div className="detail-tech-pills">
                {project.tech.map((tech, idx) => (
                  <span key={idx} className="project-tech-pill">{tech}</span>
                ))}
              </div>
            </div>

            {/* Links & Actions */}
            {project.links && project.links.length > 0 && (
              <div className="detail-actions-section">
                <h3 className="detail-section-heading">Project Resources</h3>
                <div className="detail-action-buttons">
                  {project.links.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      {link.label}
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="17" x2="17" y2="7"></line>
                        <polyline points="7 7 17 7 17 17"></polyline>
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Social Share / Connect */}
            <div className="detail-connect-strip">
              <span className="connect-label">Connect with Developer</span>
              <div className="detail-socials">
                <a href="https://github.com/Wigibles" target="_blank" rel="noopener noreferrer" className="social-pill" title="GitHub">
                  <img src={githubIcon} alt="GitHub" className="social-pill-icon" />
                  <span>GitHub</span>
                </a>
                <a href="https://www.linkedin.com/in/luigiatlinkdin/" target="_blank" rel="noopener noreferrer" className="social-pill" title="LinkedIn">
                  <img src={linkedinIcon} alt="LinkedIn" className="social-pill-icon" />
                  <span>LinkedIn</span>
                </a>
                <a href="https://www.facebook.com/luigisantiago231" target="_blank" rel="noopener noreferrer" className="social-pill" title="Facebook">
                  <img src={facebookIcon} alt="Facebook" className="social-pill-icon" />
                  <span>Facebook</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProjectDetail;